import {toBmCalendarDay} from '../active-payment-frequencies/bm-active-payment-frequencies';
import {BM_PAYMENT_FREQUENCY_LABELS, resolveBmPaymentEnumLabel} from '../payment-enum-labels/bm-payment-enum-labels';
import {
    bmClassifyContractLink,
    bmContractLinkEndedLabel,
    bmContractLinkFutureLabel,
    bmFormatCalendarDayDe
} from './bm-contract-link-visibility';
import {
    BM_CONTRACT_LINK_ENDED_TAG_STYLE_CLASS,
    BM_CONTRACT_LINK_FUTURE_TAG_STYLE_CLASS,
    BM_CONTRACT_STATUS_FALLBACK_ACTIVE_STYLE_CLASS,
    BM_CONTRACT_STATUS_FALLBACK_ENDED_STYLE_CLASS,
    bmContractStatusTagStyleClass
} from './bm-contract-status-tag-style';
import type {BmContractLinkDisplaySource} from './bm-contract-link-display-source';
import type {BmContractLinkRow} from './bm-contract-link-row';
import type {BmContractLinkTag} from './bm-contract-link-tag';

/**
 * AI:
 * Status: "in progress"
 * Type: Logic
 * SubType: Display model
 * Reason: Builds the rows of the Verträge tab of a billing account ({@link BmContractLinkRow}) - one per
 *   bm.ContractPaymentAccount (an assignment slice of a contract to this account), with every cell text
 *   and tag style derived ONCE when the row set is rebuilt (load, switch), so the template binds
 *   primitives of a stable array only.
 *
 *   Columns (TM-1000002026): VSNR | Partner | Typ | Frequenz | Beitrag | Zugeordnet ab | Zugeordnet bis |
 *   Vertragsende | Status.
 *   - Partner / Typ: the SHORT name (`Partner.shortName`, `ContractType.shortName`, max. 14 characters),
 *     else the long name; the long name is always the tooltip. A blank short name counts as none.
 *   - Frequenz: the assignment's own `frequency` (bm.PaymentFrequency), German label of the one map.
 *   - Zugeordnet ab / bis: the assignment's OWN period - not the contract's. The open end (9999-12-31,
 *     or no end) reads „unbefristet", like the bank slices in the tab „Bankverbindungen & IBANs".
 *   - Vertragsende: the contract's end; null (template: „Laufend") for no end and for the high date.
 *   - Status: the contract's base status in its enum colours (bm-contract-status-tag-style), else the
 *     fallback Aktiv/Beendet by the contract's end; next to it the assignment tag „ab {Datum}" of a slice
 *     that has not started yet or „bis {Datum}" of a slice this account no longer bills.
 *
 * @ticket TM-1000002026
 */

/** Text of a cell without a value. */
export const BM_CONTRACT_LINK_NO_VALUE = '-';

/** Text of an open assignment end. */
export const BM_CONTRACT_LINK_OPEN_END_LABEL = 'unbefristet';

/**
 * Text of the fallback status tags (no base status delivered on the contract) - title case like the
 * enum's own labels („Aktiv", „Inaktiv"), not the former upper case AKTIV / BEENDET.
 */
export const BM_CONTRACT_STATUS_FALLBACK_ACTIVE_LABEL = 'Aktiv';
export const BM_CONTRACT_STATUS_FALLBACK_ENDED_LABEL = 'Beendet';

/** Tooltip of the tag „ab {Datum}". */
export const BM_CONTRACT_LINK_FUTURE_TAG_TOOLTIP =
    'Künftige Zuordnung – der Vertrag wird ab diesem Datum über dieses Konto abgerechnet';

/** Tooltip of the tag „bis {Datum}". */
export const BM_CONTRACT_LINK_ENDED_TAG_TOOLTIP =
    'Frühere Zuordnung – bis zu diesem Datum wurde der Vertrag über diese Zuordnung abgerechnet, danach über '
    + 'ein anderes Abrechnungskonto oder eine neuere Zuordnung';

/** The high date 9999-12-31 as a calendar day: an end on or after it is no end. */
const BM_HIGH_DATE_DAY = 99991231;

/**
 * The text of a name column and its tooltip: the short name, else the long name, else „-"; the long
 * name as the tooltip (null when there is none). The long name is the master record's own name, else
 * the DTO's display name of the reference.
 */
export function bmShortNameWithLongNameTooltip(
    named: {name?: string | null; shortName?: string | null} | null | undefined,
    referenceName: string | null | undefined
): {label: string; tooltip: string | null} {
    const longName = named?.name?.trim() || referenceName?.trim() || null;
    const shortName = named?.shortName?.trim() || null;
    return {label: shortName || longName || BM_CONTRACT_LINK_NO_VALUE, tooltip: longName};
}

/**
 * An end date for display: dd.MM.yyyy, or null for the open end - no end, an unparseable end, or the
 * high date 9999-12-31 (and later).
 */
export function bmEndDateForDisplay(value: Date | string | null | undefined): string | null {
    const day = toBmCalendarDay(value);
    if (day == null || day >= BM_HIGH_DATE_DAY) {
        return null;
    }
    return bmFormatCalendarDayDe(value);
}

/** One tag without icon. */
function tagOf(label: string, styleClass: string, tooltip: string | null): BmContractLinkTag {
    return {label, icon: null, styleClass, tooltip};
}

/** The status tag: the contract's base status in its enum colours, else Aktiv / Beendet by the contract's end. */
function statusTagOf(link: BmContractLinkDisplaySource, today: Date): BmContractLinkTag {
    const contract = link?.contractDto;
    const statusName = contract?.baseStatusEnumName?.trim();
    if (statusName) {
        return {
            label: statusName,
            icon: contract?.baseStatusEnumImage?.trim() || null,
            styleClass: bmContractStatusTagStyleClass(contract),
            tooltip: null
        };
    }
    // the high date is no end; an end today still runs (same bound as bmClassifyContractLink)
    const contractEnd = toBmCalendarDay(contract?.endDate);
    const contractEnded = contractEnd != null && contractEnd < (toBmCalendarDay(today) as number);
    return contractEnded
        ? tagOf(BM_CONTRACT_STATUS_FALLBACK_ENDED_LABEL, BM_CONTRACT_STATUS_FALLBACK_ENDED_STYLE_CLASS, null)
        : tagOf(BM_CONTRACT_STATUS_FALLBACK_ACTIVE_LABEL, BM_CONTRACT_STATUS_FALLBACK_ACTIVE_STYLE_CLASS, null);
}

/** The assignment tag „ab {Datum}" (not started yet) or „bis {Datum}" (no longer billed here), else null. */
function assignmentTagOf(link: BmContractLinkDisplaySource, today: Date): BmContractLinkTag | null {
    const futureLabel = bmContractLinkFutureLabel(link, today);
    if (futureLabel) {
        return tagOf(futureLabel, BM_CONTRACT_LINK_FUTURE_TAG_STYLE_CLASS, BM_CONTRACT_LINK_FUTURE_TAG_TOOLTIP);
    }
    const endedLabel = bmContractLinkEndedLabel(link, today);
    if (endedLabel) {
        return tagOf(endedLabel, BM_CONTRACT_LINK_ENDED_TAG_STYLE_CLASS, BM_CONTRACT_LINK_ENDED_TAG_TOOLTIP);
    }
    return null;
}

/** Builds the display row of one link row against `today` (default: now). */
export function bmBuildContractLinkRow<T extends BmContractLinkDisplaySource>(
    link: T,
    today: Date = new Date()
): BmContractLinkRow<T> {
    const contract = link?.contractDto;
    const partner = bmShortNameWithLongNameTooltip(contract?.partnerDto, contract?.partnerDtoName);
    const contractType = bmShortNameWithLongNameTooltip(contract?.contractTypeDto, contract?.contractTypeDtoName);
    const id = link?.id;
    return {
        key: id == null ? null : Number(id),
        link,
        state: bmClassifyContractLink(link, today),
        partnerLabel: partner.label,
        partnerTooltip: partner.tooltip,
        contractTypeLabel: contractType.label,
        contractTypeTooltip: contractType.tooltip,
        frequencyLabel: resolveBmPaymentEnumLabel(BM_PAYMENT_FREQUENCY_LABELS, link?.frequency),
        assignedFromLabel: bmFormatCalendarDayDe(link?.startDate) ?? BM_CONTRACT_LINK_NO_VALUE,
        assignedToLabel: bmEndDateForDisplay(link?.endDate) ?? BM_CONTRACT_LINK_OPEN_END_LABEL,
        contractEndLabel: bmEndDateForDisplay(contract?.endDate),
        statusTag: statusTagOf(link, today),
        assignmentTag: assignmentTagOf(link, today)
    };
}

/** The display rows of the given link rows, in the given order. Never null. */
export function bmBuildContractLinkRows<T extends BmContractLinkDisplaySource>(
    links: T[] | null | undefined,
    today: Date = new Date()
): BmContractLinkRow<T>[] {
    return (links ?? []).map(link => bmBuildContractLinkRow(link, today));
}
